/* eslint-disable @typescript-eslint/no-explicit-any */

import moment from "moment";
import React, { ComponentProps, FC, useEffect, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { Redirect } from "react-router-dom";
import { PlotEvent } from "@ant-design/charts";
import { useDeepCompareEffect } from "use-deep-compare";
import * as _ from "lodash";
import ReactJson from "react-json-view";

import { Enterprise as Content, useTranslation } from "@ctra/i18n";
import { GAEvent } from "@ctra/analytics";
import { clampChartUnitList, isDebugMode, isMutableRef, Nullable, resizeBase64Image } from "@ctra/utils";

import {
  Charts,
  ExtendedEventEntity,
  MetricEntity,
  Breadcrumbs,
  Enterprise,
  EventSourceType,
  ChartEntity,
  DataDescriptorEntity,
  ISODuration,
  DataDictionary,
  ChartTimePeriod,
  V3DisplayFilter
} from "@ctra/api";

import {
  Charts as ChartUI,
  CtraLayout,
  SwitchWithLabel,
  Row,
  Col,
  Typography,
  Button,
  ArrowLeftOutlined,
  ShareAltOutlined
} from "@ctra/components";

import { useFarm } from "@farms";
import { CompiledRoutes, Routes } from "@routes";
import { EventListContext } from "@events";
import { useDataDictionary } from "@base";
import { useCurrentUser } from "@auth";

import {
  useSavedCharts,
  GACategories,
  GAActions,
  formatFilters,
  InternalisedCard,
  makeDefaultDataFilters
} from "@analytics";

import {
  Chart,
  ChartAPIContext,
  ChartFilterContext,
  EventList,
  FilterMetadata,
  ISODurationFilter,
  SeriesDefinition,
  useTimePeriod
} from "@chart";

import { CorrelationList } from "../CorrelationList";
import { OverlayManager } from "../OverlayManager";
import styles from "./BrowsePage.module.less";

const { WidgetWrapper, ContentWrapper } = CtraLayout;
const { ChartVariant } = ChartUI;
const { Paragraph } = Typography;

const {
  components: { Breadcrumb }
} = Breadcrumbs;

const {
  analytics: {
    correlations: { pageDescription, events },
    v3: {
      config: { cta }
    },
    config: {
      metric: { displayName }
    }
  },
  layouts: {
    dashboard: { compareCharts }
  }
} = Content;

/**
 * Get the initial ISO duration
 * @param {InternalisedCard} card
 * @param {DataDescriptorEntity} variant
 * @returns {Nullable<ISODuration>}
 */
const resolveIsoDuration = (
  card?: InternalisedCard,
  variant?: DataDescriptorEntity
): Nullable<ISODuration> => {
  if (card?.dateRange) {
    if (_.size(card.dateRange) === 1) {
      return _.first(card.dateRange) as ISODuration;
    } else {
      return null;
    }
  } else if (variant) {
    return _.get(variant, ["valueProperties", "defaultInterval"], DataDictionary.DefaultTimePeriod);
  }

  return DataDictionary.DefaultTimePeriod;
};

/**
 * Get the initial time unit
 * @param {InternalisedCard} card
 * @param {DataDescriptorEntity} variant
 * @returns {ISODuration}
 */
const resolveBucketSize = (card?: InternalisedCard, variant?: DataDescriptorEntity): ISODuration => {
  let baseBucketSize;

  const isoDurationList = clampChartUnitList(
    _.defaultTo(resolveIsoDuration(card, variant), DataDictionary.DefaultTimePeriod)
  );

  if (card) {
    baseBucketSize = _.get(card, ["bucketSize"], DataDictionary.DefaultDataInterval);
  } else if (variant) {
    const defaultBucketSize = _.get(variant, ["valueProperties", "defaultBucketSize"]);

    baseBucketSize = _.get(variant, ["valueProperties", "baseTimeGranularity"], defaultBucketSize);
  } else {
    baseBucketSize = DataDictionary.DefaultDataInterval;
  }

  return _.includes(isoDurationList, baseBucketSize)
    ? baseBucketSize
    : _.defaultTo(_.first(isoDurationList), DataDictionary.DefaultDataInterval);
};

/**
 * Compare charts page
 * @return {JSX.Element}
 * @constructor
 */
export const BrowsePage: FC = () => {
  const { t } = useTranslation();
  const { state } = useLocation();
  const { farm: farmContext, site: siteContext } = useFarm();

  const {
    meta: { isAdmin },
    api: { isFarmerOf }
  } = useCurrentUser();

  const {
    dataDescriptors,
    meta: { isLoading },
    groups: { projectionMetrics },
    api: { resolveMetricByVariantID, getChartInfo }
  } = useDataDictionary();

  const { savedCharts: allCharts } = useSavedCharts();

  const { dashboard: groupID, chartID } = useParams<{
    dashboard: string;
    chartID: string;
  }>();

  const [eventList, setEventList] = useState<ExtendedEventEntity[]>([]);

  /**
   * Tell whether to show the events
   */
  const [showEvents, setShowEvents] = useState<Record<EventSourceType, boolean>>(
    _.mapValues({ ..._.invert(_.toPlainObject(EventSourceType)) }, () => false) as Record<
      EventSourceType,
      boolean
    >
  );

  /**
   * Set up a ref for graph image conversion
   * @type {React.MutableRefObject<any>}
   */
  const ref = useRef<any>();

  /**
   * Make a ref to store sharing info with the button on the page
   * @type {React.MutableRefObject<{chart: ChartEntity, requestURL: string, filterContext: Record<string, unknown>, dataDescriptorID: DataDescriptorEntity["id"]} | undefined>}
   */
  const shareInfo = useRef<{
    chartID: ChartEntity["id"];
    requestURL: Array<Nullable<string>>;
    filterContext: Record<string, unknown>;
    dataDescriptorID: DataDescriptorEntity["id"];
    layers: DataDescriptorEntity["id"][];
    metricName: string;
    // @ts-expect-error
  }>({});

  /**
   * Find the current group by ID
   * @type {{id: string} | undefined}
   */
  const group = _.find(allCharts, {
    id: groupID
  });

  const groupName = _.get(group, "groupName");
  const savedCards = _.get(group, "cards", []);

  const currentCard = _.find(savedCards, {
    id: chartID
  });

  if (!currentCard) {
    throw new Error(
      "Unable to find card in the layout which the URL refers to. This is no-op, the app will redirect to the dashboard."
    );
  }

  const clientParams = _.get(currentCard, "clientParams", {});
  const initialMetrics = _.map(currentCard.variantIDs, resolveMetricByVariantID);
  const initialVariants = _.map(currentCard.variantIDs, (id) => dataDescriptors[id]);

  /**
   * State to keep the list of metrics
   */
  const [chartCollection, setChartCollection] = useState<
    Array<{ metricID: MetricEntity["id"]; variantID: DataDescriptorEntity["id"] }>
  >(
    initialMetrics && initialVariants
      ? _.compact(
          _.map(_.zip(initialMetrics, initialVariants), ([metric, variant]) =>
            metric && variant
              ? {
                  metricID: metric.id,
                  variantID: variant.id
                }
              : null
          )
        )
      : []
  );

  /**
   * Tell whether the event panel should be hidden
   * @type {boolean}
   */
  const isEventPanelHidden = _.isEmpty(eventList) || _.isEmpty(chartCollection);

  /**
   * Tell whether the chart is a projection metric
   * @type {boolean}
   */
  const isProjectionMetric = _.includes(projectionMetrics, _.first(chartCollection)?.metricID);

  /**
   * Get the data descriptor
   */
  const variantID = _.get(_.first(chartCollection), "variantID");

  /**
   * dateRange can either b iso duration in an array e.g ["P1M"] or a custom range e.g. ["2023-05-01", "2023-06-01"]
   * Supply props to api context based on length of array
   */
  const initialIsoDuration = resolveIsoDuration(currentCard, variantID ? dataDescriptors[variantID] : void 0);

  /**
   * Set the initial time unit based on the current card or the variant
   */
  const initialBucketSize = resolveBucketSize(currentCard, variantID ? dataDescriptors[variantID] : void 0);
  const [startDate, endDate] = _.size(currentCard.dateRange) === 2 ? currentCard.dateRange : [];
  const [isoDuration, setISOduration] = useState<Nullable<ISODuration>>(initialIsoDuration);
  const [initialTimePeriod] = useTimePeriod({ isoDuration }, _.defaultTo(0, 86400));

  const [timePeriod, setTimePeriod] = useState<ChartTimePeriod>(
    startDate && endDate ? { startDate, endDate } : initialTimePeriod
  );

  const [bucketSize, setBucketSize] = useState<ISODuration>(initialBucketSize);

  /**
   * Resolve the series filter from the current card
   * @type {Record<DataDescriptorEntity["id"], V3DisplayFilter>}
   */
  const seriesFilter: Record<DataDescriptorEntity["id"], V3DisplayFilter> = _.omitBy(
    _.mapValues(currentCard.displayFilters, (filters) =>
      _.find(filters, {
        type: "series"
      })
    ),
    _.isUndefined
  ) as Record<DataDescriptorEntity["id"], V3DisplayFilter>;

  /**
   * Get the filter values
   * @type {Record<DataDescriptorEntity["id"], Array<string>>}
   */
  const seriesValues: Record<DataDescriptorEntity["id"], Array<string>> = _.mapValues(
    seriesFilter,
    (filter) => _.get(filter, "values", [])
  );

  /**
   * Format the data filters from the current card
   * @type {Record<DataDescriptorEntity["id"], Record<string, unknown>>}
   */
  const dataFilters = formatFilters(
    _.merge(_.defaultTo(currentCard.dataFilters, {}), makeDefaultDataFilters(initialVariants, siteContext))
  );

  /**
   * Tell if the data descriptor supports multi data interval
   */
  const isMultiDataIntervalSupported = variantID
    ? _.get(dataDescriptors, [variantID, "flags", "isMultiDataIntervalSupported"])
    : false;

  /**
   * Handle adding new metric from suggested correlations to the list
   * @param {MetricEntity["id"]} variantID
   */
  const addCorrelation = (variantID: MetricEntity["id"]) => {
    const { id: metricID } = resolveMetricByVariantID(variantID);

    setChartCollection((list) => [...list, { metricID, variantID }]);
  };

  /**
   * If projection metric, add a month to the end date
   */
  useEffect(() => {
    if (isProjectionMetric) {
      initialTimePeriod.endDate = moment(initialTimePeriod.endDate).add(1, "months").toISOString();
      setISOduration(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProjectionMetric]);

  /**
   * Reset the event list when the chart list changes
   */
  useDeepCompareEffect(() => {
    setEventList([]);
  }, [chartCollection]);

  /**
   * Reset the event list when the show events change
   */
  useDeepCompareEffect(() => {
    if (eventList.length > 0 && !_.some(showEvents)) {
      setEventList([]);
    }
  }, [showEvents]);

  /**
   * Update the time period when the iso duration changes
   */
  useDeepCompareEffect(() => {
    const bucketSizeList = clampChartUnitList(isoDuration || timePeriod);

    if (!_.includes(bucketSizeList, bucketSize)) {
      setBucketSize(_.first(bucketSizeList) as ISODuration);
    }
  }, [timePeriod, isoDuration, bucketSize]);

  /**
   * Add a new chart to the list
   * @param {Array<{metricID: MetricEntity["id"], variantID: DataDescriptorEntity["id"]}>} items
   */
  const handleChange = (
    items: Array<{ metricID: MetricEntity["id"]; variantID: DataDescriptorEntity["id"] }>
  ) => {
    setChartCollection(items);
  };

  /**
   * Go back to the previous page
   */
  const onGoBack: ComponentProps<typeof Button>["onClick"] = () => {
    Enterprise.history.push({
      pathname: CompiledRoutes.app.analytics.dashboard.index({
        dashboard: groupID
      }),
      state
    });
  };

  /**
   * Share the chart
   */
  const onShareChart: ComponentProps<typeof Button>["onClick"] = isMutableRef<any>(ref)
    ? async (e) => {
        e.preventDefault();
        e.stopPropagation();

        const base64 = await resizeBase64Image(ref.current.toDataURL("image/png"), 1280);

        Enterprise.history.push({
          state: {
            shareChart: {
              ...shareInfo.current,
              instance: +new Date(),
              farmID: farmContext?.id,
              base64Content: base64
            }
          }
        });
      }
    : void 0;

  const [mainMetric] = initialMetrics || [];
  const [mainVariant] = initialVariants || [];

  /**
   * Get the chart information from the initial metric and variant
   * @type {ChartInfo | null}
   */
  const chartInfo = mainMetric && mainVariant ? getChartInfo(mainMetric.id, mainVariant?.id) : null;

  /**
   * Get the translated metric name
   * @type {string}
   */
  const metricName = chartInfo
    ? t(displayName(chartInfo.nameToDisplay), {
        makeDefaultValue: true
      })
    : t(compareCharts.title);

  /**
   * Make a correlation component
   * @type {JSX.Element | null}
   */
  const correlationsComponent = _.size(chartCollection) ? (
    <CorrelationList
      isoDuration={isoDuration}
      variantList={_.map(chartCollection, "variant")}
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      variantID={_.last(chartCollection)!.variantID}
      onAdd={addCorrelation}
    />
  ) : null;

  return (
    <EventListContext.Provider farmID={farmContext?.id} siteID={siteContext?.id}>
      {groupName && (
        <Breadcrumb
          path={CompiledRoutes.app.analytics.dashboard.index({
            dashboard: groupID
          })}
          title={groupName}
        />
      )}
      <Breadcrumb title={metricName} />
      <ChartFilterContext.Provider
        isoDuration={isoDuration}
        bucketSize={bucketSize}
        timePeriod={timePeriod}
        currentValues={{ isoDuration, timePeriod, bucketSize }}
        series={seriesValues}
        filters={dataFilters}
      >
        <ChartFilterContext.Consumer>
          {(filterContext) => (
            <>
              <WidgetWrapper
                omitHeader={true}
                className={styles.WidgetWrapper}
                loading={isLoading}
                title={<Paragraph>{t<string>(pageDescription)}</Paragraph>}
              >
                <Row gutter={[24, 0]} wrap={false}>
                  <Col flex={1}>
                    <Row className={styles.Options} justify="end" align="middle">
                      {farmContext && (
                        <EventListContext.Consumer>
                          {({ events: eventList }) =>
                            _.size(eventList) > 0 && (
                              <>
                                <Col>
                                  <SwitchWithLabel
                                    data-gtm-category={GACategories.configurator}
                                    data-gtm-action="Toggle aiModel annotations"
                                    // inverted the showEvents because of render delay
                                    data-gtm-info={JSON.stringify({ visible: !showEvents })}
                                    defaultChecked={showEvents[EventSourceType.aiModel]}
                                    onChange={(checked) =>
                                      setShowEvents((current) => ({
                                        ...current,
                                        [EventSourceType.aiModel]: checked
                                      }))
                                    }
                                    disabled={_.isEmpty(chartCollection)}
                                    size="small"
                                    label={t(events, {
                                      variant: "ai"
                                    })}
                                  />
                                </Col>
                                <Col>
                                  <SwitchWithLabel
                                    data-gtm-category={GACategories.configurator}
                                    data-gtm-action="Toggle events"
                                    // inverted the showEvents because of render delay
                                    data-gtm-info={JSON.stringify({ visible: !showEvents })}
                                    defaultChecked={_.every(_.omit(showEvents, EventSourceType.aiModel))}
                                    onChange={(checked) =>
                                      setShowEvents(({ AIModel, ...rest }) => ({
                                        AIModel,
                                        ..._.mapValues(rest, () => checked)
                                      }))
                                    }
                                    disabled={_.isEmpty(chartCollection)}
                                    size="small"
                                    label={t(events, { variant: "Events" })}
                                  />
                                </Col>
                              </>
                            )
                          }
                        </EventListContext.Consumer>
                      )}
                      <Col>
                        <ISODurationFilter
                          data-gtm-category={GACategories.configurator}
                          data-gtm-action="Change ISO duration"
                          durations={Charts.utils.supportedDurations}
                          duration={isoDuration}
                          buckets={
                            isMultiDataIntervalSupported &&
                            Charts.utils.hasBucketSizeSupport(currentCard.chartType)
                              ? void 0
                              : []
                          } // default time units, unless no support for multi intervals
                          timePeriod={timePeriod}
                          bucketSize={bucketSize}
                          onDurationChange={setISOduration}
                          onTimePeriodChange={setTimePeriod}
                          onUnitChange={setBucketSize}
                        />
                      </Col>
                    </Row>
                    <ContentWrapper padded={false} className={styles.Wrapper}>
                      <ChartAPIContext.Provider
                        metricVariantID={_.map(chartCollection, "variantID")}
                        farmID={farmContext?.id}
                        defaultView={currentCard.chartType}
                      >
                        <OverlayManager
                          initialValues={{ items: chartCollection }}
                          onSubmit={() => void 0}
                          onChange={({ items }) => {
                            handleChange(items);
                          }}
                        />
                        {_.every(chartCollection, ({ variantID }) => !!dataDescriptors[variantID]) ? (
                          <ChartAPIContext.Provider
                            metricVariantID={_.map(chartCollection, "variantID")}
                            farmID={farmContext?.id}
                            defaultView={currentCard.chartType}
                          >
                            <ChartAPIContext.Consumer>
                              {({ metricVariants, charts: [{ entity }], meta: { requestURL } }) => {
                                const metricName = _.join(
                                  _.map(chartCollection, ({ metricID, variantID }) => {
                                    return t(displayName(getChartInfo(metricID, variantID).nameToDisplay), {
                                      makeDefaultValue: true
                                    });
                                  }),
                                  ", "
                                );

                                const metricVariantIDCollection = _.map(metricVariants, "id");

                                const seriesDefinitions = _.reduce(
                                  metricVariantIDCollection,
                                  (acc, id) => {
                                    const series = filterContext.api.getSeries(id);

                                    if (series && !_.isEmpty(series)) {
                                      acc[id] = series;
                                    }
                                    return acc;
                                  },
                                  {} as Record<string, SeriesDefinition>
                                );

                                const metadata = _.reduce(
                                  metricVariantIDCollection,
                                  (acc, id) => {
                                    const meta = filterContext.api.getMetadata(id);

                                    if (meta && !_.isEmpty(meta)) {
                                      acc[id] = meta;
                                    }
                                    return acc;
                                  },
                                  {} as Record<string, FilterMetadata>
                                );

                                /**
                                 * Update the share info ref
                                 * @type {{chartID: string, requestURL: string | null | undefined, filterContext: {filters?: Record<DataDescriptorEntity["id"], ChartFilters>, timePeriod?: ChartTimePeriod, isoDuration?: Nullable<ISODuration>, bucketSize?: ISODuration, currentDateMethod?: DateMethod, api?: FilterProviderAPI, series: Record<string, SeriesDefinition>, metadata: Record<string, FilterMetadata>}, dataDescriptorID: string, metricName: any, layers: any[]}}
                                 */
                                shareInfo.current = {
                                  chartID: entity.id,
                                  requestURL,
                                  filterContext: {
                                    ..._.pick(filterContext, ["filters", "timePeriod", "isoDuration"]),
                                    series: seriesDefinitions,
                                    metadata
                                  },
                                  dataDescriptorID: _.first(
                                    metricVariantIDCollection
                                  ) as DataDescriptorEntity["id"],
                                  metricName,
                                  layers: metricVariantIDCollection
                                };

                                return (
                                  <Row wrap={false}>
                                    <Col span={24} className={styles.Chart}>
                                      <div className={styles.MetricName}>
                                        <Button type="text">{metricName}</Button>
                                      </div>
                                      <Chart
                                        ref={ref}
                                        key={chartCollection.length}
                                        variant={ChartVariant.V3}
                                        viewOptions={{
                                          showEvents,
                                          zoomEnabled: false,
                                          showSupportedViews: true,
                                          showToolbar: false,
                                          showAnnotations: true
                                        }}
                                        config={{
                                          ...clientParams,
                                          onReady: (plot) => {
                                            ref.current = plot;

                                            plot.on("element:click", (event: PlotEvent) => {
                                              const shape = _.get(event, ["data", "shape"]);
                                              const eventList = _.get(event, ["data", "data", "events"]);

                                              GAEvent(
                                                GACategories.configurator,
                                                "Open event",
                                                JSON.stringify({ eventList })
                                              );

                                              if (shape === "farm-event-point") {
                                                setEventList(() => eventList);
                                              }
                                            });
                                          },
                                          ...(chartCollection.length === 1 ? { height: 450 } : {})
                                        }}
                                      />
                                    </Col>
                                  </Row>
                                );
                              }}
                            </ChartAPIContext.Consumer>
                          </ChartAPIContext.Provider>
                        ) : (
                          <Redirect to={Routes.app.analytics.dashboard.index} />
                        )}
                      </ChartAPIContext.Provider>
                      <Row>
                        <Col>{correlationsComponent}</Col>
                      </Row>
                    </ContentWrapper>
                  </Col>
                  {isEventPanelHidden ? null : (
                    <Col span={5}>
                      <EventList
                        variant={
                          _.every(eventList, (event) => event.source.type === EventSourceType.aiModel)
                            ? EventList.variant.insights
                            : EventList.variant.events
                        }
                        data={eventList}
                        handleClose={() => setEventList([])}
                      />
                    </Col>
                  )}
                </Row>
              </WidgetWrapper>
            </>
          )}
        </ChartFilterContext.Consumer>
        {isDebugMode() ? (
          <div onClick={(e) => e.stopPropagation()}>
            <ReactJson
              style={{
                border: "1px dashed #ccc",
                padding: 16,
                margin: "32px 0",
                maxHeight: 300,
                overflow: "scroll"
              }}
              src={{
                chartRefMutable: isMutableRef<any>(ref),
                shareInfo: shareInfo.current
              }}
            />
          </div>
        ) : null}
        <Row justify="space-between">
          <Col>
            <Button size="large" type="secondary" onClick={onGoBack} icon={<ArrowLeftOutlined />}>
              {t(cta.back)}
            </Button>
          </Col>
          {isAdmin || isFarmerOf(farmContext?.id) ? (
            <Col>
              <Button
                type="link"
                data-gtm-category={GACategories.sharing}
                data-gtm-action={GAActions.shareChart}
                data-gtm-label={shareInfo.current.metricName}
                icon={<ShareAltOutlined />}
                onClick={onShareChart}
              >
                {t<string>(cta.share)}
              </Button>
            </Col>
          ) : null}
        </Row>
      </ChartFilterContext.Provider>
    </EventListContext.Provider>
  );
};
